/**
 * «Водители фирмы»: имя, возраст и пол каждого водителя и переход к его
 * документам — блок `WEB-2.4` (`FR-WEB-506`, решение формы `Б16`).
 *
 * Экран показывает **каждую** запись ответа в порядке ответа и читает только
 * поля водителя из контракта — «и ничего сверх»: ни телефона, ни почты, ни
 * даты рождения на экран не доходит, даже если бы пришло. Состав фирмы экран
 * не читает вовсе: кто водитель, решает сервер своим ответом.
 *
 * Возраст — число из ответа, как пришло: считает его сервер по календарю
 * Ташкента, и клиент ни календарных лет, ни арифметики над возрастом не ведёт.
 * Слово «лет» не ставится — склонений в пяти языках нет, подпись «Возраст:»
 * стоит перед числом. Пол — подписью строителя; значение вне перечня —
 * «неизвестно» без исходной строки (CR-007). Пустое значение — «не указан»:
 * человек ещё не назвал, и пустое место читалось бы как «поля нет».
 *
 * Кто вправе читать водителей — директор, диспетчер, механик, бухгалтер —
 * решает сервер: отказ `403` и отсутствие фирмы `404` дают одно состояние
 * «нет доступа» побайтово одним экраном (`FR-WEB-006`). Ветки по роли здесь нет.
 *
 * Документы водителя открываются прежним экраном документов записи членства:
 * идентификатор записи членства водителя и есть адрес его документов. Набор
 * видимых типов документа решает сервер (`FR-BE-407`), не этот экран.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type { CompanyDriver } from '../api/contract';
import { genderStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { memberDocumentsPath } from '../router';
import { enumLabel, placeState, screenStateFor } from './state';
import type { ScreenState } from './state';

export interface CompanyDriversScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

export interface CompanyDriversScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  /** Водители в том порядке, в котором их вернул сервер. */
  readonly drivers: readonly CompanyDriver[];
  load(): Promise<void>;
}

export function createCompanyDriversScreen(options: CompanyDriversScreenOptions): CompanyDriversScreen {
  const { client, strings, companyId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const list = document.createElement('ul');
  const note = document.createElement('p');

  element.className = 'screen screen-company-drivers';
  list.className = 'driver-list';
  note.className = 'form-hint';
  heading.textContent = strings.drivers_title;
  note.textContent = strings.drivers_self_reported;

  let state: ScreenState | null = 'loading';
  let drivers: readonly CompanyDriver[] = [];
  /** Пришёл ли ответ сервера — та же величина, что на экране фирмы. */
  let answered = false;

  /** Пол подписью: не указан — «не указан», вне перечня — «неизвестно». */
  function genderShown(driver: CompanyDriver): string {
    return driver.gender === null ? strings.drivers_not_specified : enumLabel(strings, genderStringKey(driver.gender));
  }

  function row(driver: CompanyDriver): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');
    const documents = document.createElement('a');

    node.className = 'driver-row';

    // Возраст — числом из ответа: его считает сервер, и здесь он только
    // показывается. Пустой возраст — «не указан», а не ноль и не пустое место.
    const ageShown = driver.age === null ? strings.drivers_not_specified : String(driver.age);

    line.textContent = [
      driver.name ?? strings.drivers_name_unknown,
      `${strings.drivers_age} ${ageShown}`,
      genderShown(driver),
    ].join(' · ');

    // Документы водителя — прежний экран документов записи членства. Адрес
    // собирает единственный строитель.
    documents.className = 'company-link';
    documents.href = memberDocumentsPath(companyId, driver.membership_id);
    documents.textContent = strings.drivers_documents;

    node.append(line, documents);

    return node;
  }

  function render(): void {
    list.replaceChildren(...drivers.map(row));
    // Правило места состояния — одна функция на все экраны (долг `TD-66`):
    // ответа нет — показывать нечего вовсе, отказ прав и отсутствие фирмы дают
    // побайтово один экран; ответ пустой — «водителей пока нет» встаёт на место
    // **списка**, заголовок остаётся.
    placeState(
      element,
      { state, answered, frame: [heading], list, after: [note], emptyCaption: strings.drivers_empty },
      strings,
    );
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    drivers = [];
    answered = false;
    render();

    try {
      const received = await client.listCompanyDrivers(companyId);

      drivers = received;
      answered = true;
      state = screenStateFor({ kind: 'done', empty: received.length === 0 });
    } catch (error) {
      drivers = [];
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get drivers(): readonly CompanyDriver[] {
      return drivers;
    },

    load,
  };
}
